// Laser Beam Room: 2-D geometric-optics ray tracer (reflection, Snell refraction with Cauchy dispersion,
// total internal reflection, Fresnel (Schlick) partial reflection, thin lenses, gratings, filters, absorption).
export const W = 16, H = 10;

export const LASERS = {
  hene: { label: "HeNe 633 nm (red)", nm: [633] },
  green: { label: "DPSS 532 nm (green)", nm: [532] },
  blue: { label: "Diode 450 nm (blue)", nm: [450] },
  violet: { label: "Diode 405 nm (violet)", nm: [405] },
  uv: { label: "Nd:YAG ×3 355 nm (UV)", nm: [355], invisible: true },
  ir: { label: "Nd:YAG 1064 nm (infrared)", nm: [1064], invisible: true },
  co2: { label: "CO₂ 10.6 µm (far infrared)", nm: [10600], invisible: true },
  white: { label: "White supercontinuum", nm: [410, 450, 490, 530, 570, 610, 650, 690] },
};
export const ELEMENTS = {
  laser: "Laser", mirror: "Mirror", splitter: "Beam splitter", prism: "Prism", block: "Glass block",
  lens: "Converging lens", diverge: "Diverging lens", grating: "Diffraction grating", filter: "Colour filter", detector: "Power meter", absorber: "Beam dump",
};
export const FILTERS = { red: [590, 750], green: [495, 570], blue: [420, 495] };

export function nmToRgb(nm) {
  if (nm > 780) return [255, 40, 30];
  if (nm < 380) return [170, 80, 255];
  let r = 0, g = 0, b = 0;
  if (nm < 440) { r = -(nm - 440) / 60; b = 1; } else if (nm < 490) { g = (nm - 440) / 50; b = 1; } else if (nm < 510) { g = 1; b = -(nm - 510) / 20; }
  else if (nm < 580) { r = (nm - 510) / 70; g = 1; } else if (nm < 645) { r = 1; g = -(nm - 645) / 65; } else r = 1;
  return [r * 255, g * 255, b * 255].map(Math.round);
}
// Cauchy dispersion for crown glass (BK7-like): n = A + B/λ²  (λ in µm)
export const indexOf = (nm, glass = 1) => 1.5046 * glass + 0.0042 / ((nm / 1000) ** 2);
const glassAbsorbs = (nm) => nm > 4000 || nm < 320;

let uid = 1;
export const makeEl = (type, x = W / 2, y = H / 2, angle = 0, extra = {}) => ({
  id: uid++, type, x, y, angle,
  ...(type === "laser" ? { kind: "hene", power: 5, on: true, pulsed: false } : {}),
  ...(type === "filter" ? { band: "red" } : {}),
  ...(type === "lens" || type === "diverge" ? { focal: 2.5 } : {}),
  ...(type === "grating" ? { lines: 600 } : {}),
  ...(type === "prism" || type === "block" ? { size: type === "prism" ? 1.8 : 1.6 } : {}),
  ...extra,
});

export const PRESETS = {
  rainbow: () => [makeEl("laser", 1, 9, -0.8, { kind: "white", power: 8 }), makeEl("prism", 6.5, 5, 1.3, { size: 2.2 }), makeEl("detector", 15.3, 5.25)],
  maze: () => [makeEl("laser", 1, 1.4, 0, { kind: "green" }), makeEl("mirror", 13.5, 1.4, -Math.PI / 4), makeEl("mirror", 13.5, 8.4, Math.PI / 4),
    makeEl("splitter", 8, 8.4, -Math.PI / 4), makeEl("mirror", 8, 4.6, -Math.PI / 4), makeEl("lens", 4, 4.6, 0), makeEl("detector", 1.2, 4.6), makeEl("detector", 1.2, 8.4)],
  spectrometer: () => [makeEl("laser", 1, 5, 0, { kind: "white", power: 8 }), makeEl("lens", 3.2, 5, 0, { focal: 2 }), makeEl("grating", 7, 5, 0, { lines: 900 }), makeEl("detector", 14.5, 5)],
  co2: () => [makeEl("laser", 1, 3, 0, { kind: "co2", power: 40 }), makeEl("block", 6, 3, 0), makeEl("laser", 1, 7, 0, { kind: "hene" }), makeEl("block", 6, 7, 0), makeEl("detector", 14, 3), makeEl("detector", 14, 7)],
  interfere: () => [makeEl("laser", 1, 5, 0, { kind: "blue" }), makeEl("splitter", 6, 5, Math.PI / 4), makeEl("mirror", 12, 5, 0), makeEl("mirror", 6, 1.2, Math.PI / 2),
    makeEl("filter", 9, 5, 0, { band: "blue" }), makeEl("detector", 6, 9.2)],
};

const rot = (x, y, a) => [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];

// surfaces of an element in world space: {a:[x,y], b:[x,y], el, solid?}
function surfaces(el) {
  const P = (lx, ly) => { const [x, y] = rot(lx, ly, el.angle); return [el.x + x, el.y + y]; };
  const seg = (lx1, ly1, lx2, ly2) => ({ a: P(lx1, ly1), b: P(lx2, ly2), el });
  switch (el.type) {
    case "mirror": case "splitter": case "filter": case "grating": case "lens": case "diverge": return [seg(0, -0.75, 0, 0.75)];
    case "detector": return [seg(-0.3, -0.3, -0.3, 0.3), seg(0.3, -0.3, 0.3, 0.3), seg(-0.3, -0.3, 0.3, -0.3), seg(-0.3, 0.3, 0.3, 0.3)];
    case "absorber": return [seg(-0.3, -0.5, -0.3, 0.5), seg(0.3, -0.5, 0.3, 0.5), seg(-0.3, -0.5, 0.3, -0.5), seg(-0.3, 0.5, 0.3, 0.5)];
    case "laser": return [seg(-0.7, -0.22, -0.7, 0.22), seg(-0.7, 0.22, 0.25, 0.22), seg(-0.7, -0.22, 0.25, -0.22)];
    case "prism": { const s = el.size, h = (s * Math.sqrt(3)) / 2, v = [[-s / 2, h / 3], [s / 2, h / 3], [0, (-2 * h) / 3]].map(([x, y]) => P(x, y)); return v.map((p, i) => ({ a: p, b: v[(i + 1) % 3], el, solid: true })); }
    case "block": { const w = el.size, hh = 0.55, v = [[-w / 2, -hh], [w / 2, -hh], [w / 2, hh], [-w / 2, hh]].map(([x, y]) => P(x, y)); return v.map((p, i) => ({ a: p, b: v[(i + 1) % 4], el, solid: true })); }
    default: return [];
  }
}
function hitSeg(ox, oy, dx, dy, s) {
  const ex = s.b[0] - s.a[0], ey = s.b[1] - s.a[1], den = dx * ey - dy * ex;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((s.a[0] - ox) * ey - (s.a[1] - oy) * ex) / den, u = ((s.a[0] - ox) * dy - (s.a[1] - oy) * dx) / den;
  return t > 1e-6 && u >= 0 && u <= 1 ? { t, u, ex, ey } : null;
}
const inside = (x, y, el) => {
  const ss = surfaces(el); let c = false;
  for (const s of ss) { const [x1, y1] = s.a, [x2, y2] = s.b; if (((y1 > y) !== (y2 > y)) && x < ((x2 - x1) * (y - y1)) / (y2 - y1) + x1) c = !c; }
  return c;
};

export function trace(elements) {
  const segsOut = [], hits = {}, meters = {}, heat = {};
  const all = elements.flatMap(surfaces);
  const queue = [];
  for (const el of elements) if (el.type === "laser" && el.on) {
    const L = LASERS[el.kind], [dx, dy] = rot(1, 0, el.angle), [ox, oy] = [el.x + dx * 0.27, el.y + dy * 0.27];
    for (const nm of L.nm) queue.push({ x: ox, y: oy, dx, dy, nm, p: el.power / L.nm.length, depth: 0, n: 1, src: el, invisible: !!L.invisible });
  }
  let rays = 0;
  while (queue.length && rays < 900) {
    const r = queue.shift(); rays++;
    let best = null;
    for (const s of all) { if (s.el === r.src && r.depth === 0 && s.el.type === "laser") continue; const h = hitSeg(r.x, r.y, r.dx, r.dy, s); if (h && (!best || h.t < best.t)) best = { ...h, s }; }
    // room walls
    let tw = Infinity;
    if (r.dx > 0) tw = Math.min(tw, (W - r.x) / r.dx); if (r.dx < 0) tw = Math.min(tw, -r.x / r.dx);
    if (r.dy > 0) tw = Math.min(tw, (H - r.y) / r.dy); if (r.dy < 0) tw = Math.min(tw, -r.y / r.dy);
    if (!best || best.t > tw) { segsOut.push({ x1: r.x, y1: r.y, x2: r.x + r.dx * tw, y2: r.y + r.dy * tw, nm: r.nm, p: r.p, inv: r.invisible, wall: true, src: r.src.id }); continue; }
    const hx = r.x + r.dx * best.t, hy = r.y + r.dy * best.t, el = best.s.el;
    segsOut.push({ x1: r.x, y1: r.y, x2: hx, y2: hy, nm: r.nm, p: r.p, inv: r.invisible, src: r.src.id });
    if (r.depth > 40 || r.p < 0.004) continue;
    const len = Math.hypot(best.ex, best.ey); let nx = -best.ey / len, ny = best.ex / len;
    let cosi = -(r.dx * nx + r.dy * ny); if (cosi < 0) { nx = -nx; ny = -ny; cosi = -cosi; }
    const refl = () => ({ dx: r.dx + 2 * cosi * nx, dy: r.dy + 2 * cosi * ny });
    const spawn = (o) => queue.push({ ...r, x: hx, y: hy, depth: r.depth + 1, ...o });
    const thetaI = (Math.acos(Math.min(1, cosi)) * 180) / Math.PI;
    const glassy = ["prism", "block", "lens", "diverge", "splitter", "filter", "grating"].includes(el.type);
    if (glassy && glassAbsorbs(r.nm)) { heat[el.id] = (heat[el.id] || 0) + r.p; hits[el.id] = { thetaI, note: `${r.nm >= 1000 ? (r.nm / 1000).toFixed(1) + " µm" : r.nm + " nm"} absorbed by glass` }; continue; }
    switch (el.type) {
      case "mirror": hits[el.id] = { thetaI, thetaR: thetaI }; spawn({ ...refl(), p: r.p * 0.97 }); break;
      case "splitter": hits[el.id] = { thetaI, note: "50 % reflected / 50 % transmitted" }; spawn({ ...refl(), p: r.p * 0.5 }); spawn({ p: r.p * 0.5 }); break;
      case "absorber": heat[el.id] = (heat[el.id] || 0) + r.p; break;
      case "laser": break;
      case "detector": { const m = (meters[el.id] = meters[el.id] || { p: 0, nms: [] }); m.p += r.p; if (!m.nms.includes(r.nm)) m.nms.push(r.nm); hits[el.id] = { thetaI }; break; }
      case "filter": { const [lo, hi] = FILTERS[el.band]; if (r.nm >= lo && r.nm <= hi) spawn({ p: r.p * 0.9 }); else heat[el.id] = (heat[el.id] || 0) + r.p; hits[el.id] = { thetaI, note: `passes ${lo}–${hi} nm` }; break; }
      case "lens": case "diverge": {
        // ideal thin lens: slope change −h/f in the lens frame
        const f = (el.type === "lens" ? 1 : -1) * el.focal, [ax, ay] = rot(1, 0, el.angle);
        const h = (hx - el.x) * -ay + (hy - el.y) * ax, along = r.dx * ax + r.dy * ay, perp = r.dx * -ay + r.dy * ax, sgn = Math.sign(along) || 1;
        const np0 = perp - (h / f) * Math.abs(along), nd = Math.hypot(along, np0), na = along / nd, np = np0 / nd;
        spawn({ dx: na * ax - np * ay, dy: na * ay + np * ax, p: r.p * 0.96 }); hits[el.id] = { thetaI, note: `f = ${f.toFixed(2)} m · h = ${h.toFixed(2)}` }; break;
      }
      case "grating": {
        // grating equation: sin θm = sin θi + m λ / d
        const d = 1e6 / el.lines, [ax, ay] = rot(1, 0, el.angle), along = r.dx * ax + r.dy * ay, sgn = Math.sign(along) || 1;
        const sinI = r.dx * -ay + r.dy * ax;
        for (const m of [-1, 0, 1]) {
          const s = sinI + (m * r.nm) / d; if (Math.abs(s) >= 1) continue;
          const c = Math.sqrt(1 - s * s) * sgn; spawn({ dx: c * ax - s * ay, dy: c * ay + s * ax, p: r.p * (m === 0 ? 0.4 : 0.3) });
        }
        hits[el.id] = { thetaI, note: `d = ${(d / 1000).toFixed(3)} µm · orders −1, 0, +1` }; break;
      }
      case "prism": case "block": {
        const ng = indexOf(r.nm), entering = !inside(hx - r.dx * 1e-4, hy - r.dy * 1e-4, el);
        const n1 = entering ? 1 : ng, n2 = entering ? ng : 1, eta = n1 / n2, k = 1 - eta * eta * (1 - cosi * cosi);
        const R0 = ((n1 - n2) / (n1 + n2)) ** 2, Rs = R0 + (1 - R0) * (1 - cosi) ** 5;
        if (k < 0) { spawn({ ...refl() }); hits[el.id] = { thetaI, note: `total internal reflection (n = ${ng.toFixed(4)})` }; break; }
        const ct = Math.sqrt(k), tx = eta * r.dx + (eta * cosi - ct) * nx, ty = eta * r.dy + (eta * cosi - ct) * ny;
        spawn({ dx: tx, dy: ty, p: r.p * (1 - Rs) });
        if (r.p * Rs > 0.05) spawn({ ...refl(), p: r.p * Rs });
        hits[el.id] = { thetaI, thetaT: (Math.acos(ct) * 180) / Math.PI, n: ng, note: `${n1.toFixed(3)}·sin θ₁ = ${n2.toFixed(3)}·sin θ₂` };
        break;
      }
      default: break;
    }
  }
  for (const m of Object.values(meters)) m.nms.sort((a, b) => a - b);
  return { segs: segsOut, hits, meters, heat, rays };
}
